import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { isSameDay, startOfDay } from 'date-fns';
import { ja } from 'date-fns/locale';
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md';

import { Button } from '@/components/Button';
import { SCHEDULE_STEP_MINUTES } from '../constants';
import { getScheduleBounds, validateScheduledAt } from '../lib/scheduledAt';
import MailDialog from './MailDialog';

registerLocale('ja', ja);

const HOURS = Array.from({ length: 24 }, (_, index) => index);
const MINUTES = Array.from(
  { length: 60 / SCHEDULE_STEP_MINUTES },
  (_, i) => i * SCHEDULE_STEP_MINUTES
);

const calendarClassName = [
  String.raw`[&_.react-datepicker]:block! [&_.react-datepicker]:w-full! [&_.react-datepicker]:rounded-none! [&_.react-datepicker]:border-0! [&_.react-datepicker]:bg-white! [&_.react-datepicker]:font-[inherit]!`,
  String.raw`[&_.react-datepicker\_\_month-container]:float-none! [&_.react-datepicker\_\_month-container]:w-full!`,
  String.raw`[&_.react-datepicker\_\_header]:rounded-none! [&_.react-datepicker\_\_header]:border-0! [&_.react-datepicker\_\_header]:bg-white! [&_.react-datepicker\_\_header]:p-0!`,
  String.raw`[&_.react-datepicker\_\_day-names]:m-0! [&_.react-datepicker\_\_day-names]:flex! [&_.react-datepicker\_\_day-names]:h-[21px] [&_.react-datepicker\_\_day-names]:items-center [&_.react-datepicker\_\_day-names]:justify-between [&_.react-datepicker\_\_day-names]:px-md`,
  String.raw`[&_.react-datepicker\_\_day-name]:m-0! [&_.react-datepicker\_\_day-name]:size-6! [&_.react-datepicker\_\_day-name]:text-[12px]! [&_.react-datepicker\_\_day-name]:leading-6! [&_.react-datepicker\_\_day-name]:font-bold [&_.react-datepicker\_\_day-name]:text-[#333]!`,
  String.raw`[&_.react-datepicker\_\_day-name:first-child]:text-[#ea3323]! [&_.react-datepicker\_\_day-name:last-child]:text-primary!`,
  String.raw`[&_.react-datepicker\_\_month]:m-0! [&_.react-datepicker\_\_month]:flex [&_.react-datepicker\_\_month]:flex-col [&_.react-datepicker\_\_month]:gap-xxs [&_.react-datepicker\_\_month]:pt-xxs`,
  String.raw`[&_.react-datepicker\_\_week]:flex [&_.react-datepicker\_\_week]:justify-between [&_.react-datepicker\_\_week]:px-md`,
  String.raw`[&_.react-datepicker\_\_day]:m-0! [&_.react-datepicker\_\_day]:size-6! [&_.react-datepicker\_\_day]:rounded-full! [&_.react-datepicker\_\_day]:bg-transparent! [&_.react-datepicker\_\_day]:text-[14px]! [&_.react-datepicker\_\_day]:leading-none! [&_.react-datepicker\_\_day]:font-normal! [&_.react-datepicker\_\_day]:text-[#333]! [&_.react-datepicker\_\_day]:outline-hidden`,
  String.raw`[&_.react-datepicker\_\_day.react-datepicker\_\_day--outside-month]:text-border!`,
].join(' ');

function nextSelectableSlot(): Date {
  const { min } = getScheduleBounds();
  const slot = new Date(min);
  slot.setSeconds(0, 0);
  const remainder = slot.getMinutes() % SCHEDULE_STEP_MINUTES;
  if (remainder !== 0 || min.getSeconds() > 0 || min.getMilliseconds() > 0) {
    slot.setMinutes(slot.getMinutes() - remainder + SCHEDULE_STEP_MINUTES);
  }
  return slot;
}

function withTime(date: Date, hour: number, minute: number) {
  const next = new Date(date);
  next.setHours(hour, minute, 0, 0);
  return next;
}

function isWithinBounds(date: Date) {
  const { min, max } = getScheduleBounds();
  return date.getTime() >= min.getTime() && date.getTime() <= max.getTime();
}

type ScheduleDateTimeDialogProps = {
  open: boolean;
  title: string;
  initialValue?: Date | null;
  onClose: () => void;
  onSubmit: (date: Date) => void | Promise<void>;
  isSubmitting?: boolean;
};

export default function ScheduleDateTimeDialog({
  open,
  title,
  initialValue,
  onClose,
  onSubmit,
  isSubmitting = false,
}: ScheduleDateTimeDialogProps) {
  const [selected, setSelected] = useState<Date>(() => initialValue ?? nextSelectableSlot());
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const hourListRef = useRef<HTMLDivElement>(null);
  const minuteListRef = useRef<HTMLDivElement>(null);

  // 呼び出し元が描画ごとに新しい Date を渡しても入力中の値を戻さないよう時刻値で比較する
  const initialTime = initialValue?.getTime();
  useEffect(() => {
    if (!open) return;
    const initial = initialTime === undefined ? null : new Date(initialTime);
    setSelected(initial && isWithinBounds(initial) ? initial : nextSelectableSlot());
    setErrorMessage(null);
  }, [initialTime, open]);

  const selectedHour = selected.getHours();
  const selectedMinute = selected.getMinutes();
  const hasCenteredRef = useRef(false);

  useEffect(() => {
    if (!open) {
      hasCenteredRef.current = false;
      return;
    }
    const frame = requestAnimationFrame(() => {
      for (const list of [hourListRef.current, minuteListRef.current]) {
        const item = list?.querySelector<HTMLElement>('[aria-pressed="true"]');
        if (!list || !item) continue;
        list.scrollTo({
          top: item.offsetTop - (list.clientHeight - item.offsetHeight) / 2,
          behavior: hasCenteredRef.current ? 'smooth' : 'auto',
        });
      }
      hasCenteredRef.current = true;
    });
    return () => cancelAnimationFrame(frame);
  }, [open, selectedHour, selectedMinute]);

  const { max } = getScheduleBounds();
  const today = startOfDay(new Date());

  const renderDay = (dayOfMonth: number, date?: Date) => {
    const isDisabled = !date || date < today || startOfDay(date) > startOfDay(max);
    const isSelected = !!date && isSameDay(date, selected);
    return (
      <span
        className={clsx(
          'flex size-6 items-center justify-center rounded-full leading-none',
          isSelected && 'bg-primary text-white',
          !isSelected && !isDisabled && date && isSameDay(date, today) && 'bg-red-50 text-red-600',
          !isSelected && isDisabled && 'text-border',
          !isSelected && !isDisabled && 'hover:bg-gray'
        )}
      >
        {dayOfMonth}
      </span>
    );
  };

  const isHourSelectable = (hour: number) =>
    MINUTES.some((minute) => isWithinBounds(withTime(selected, hour, minute)));

  const handleSelectDate = (date: Date | null) => {
    if (!date) return;
    const candidate = withTime(date, selected.getHours(), selected.getMinutes());
    setErrorMessage(null);
    if (isWithinBounds(candidate)) {
      setSelected(candidate);
      return;
    }
    const firstSelectable = HOURS.flatMap((hour) =>
      MINUTES.map((minute) => withTime(date, hour, minute))
    ).find(isWithinBounds);
    setSelected(firstSelectable ?? candidate);
  };

  const handleSubmit = async () => {
    const message = validateScheduledAt(selected);
    if (message) {
      setErrorMessage(message);
      return;
    }
    await onSubmit(selected);
  };

  const timeOptionClassName = (isSelected: boolean) =>
    clsx(
      'flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full border-none p-0 font-[inherit] text-[18px] leading-none transition-colors disabled:cursor-not-allowed disabled:text-border disabled:hover:bg-transparent',
      isSelected ? 'bg-primary text-white' : 'bg-transparent text-[#333] hover:bg-gray'
    );

  return (
    <MailDialog
      open={open}
      onClose={onClose}
      title={title}
      widthClassName="w-[532px]"
      bodyClassName="p-md"
      preventClose={isSubmitting}
      footerStart={
        errorMessage && (
          <p role="alert" className="text-body-xs text-danger">
            {errorMessage}
          </p>
        )
      }
      footer={
        <>
          <Button
            type="button"
            color="secondary"
            size="md"
            onClick={onClose}
            disabled={isSubmitting}
          >
            キャンセル
          </Button>
          <Button
            type="button"
            color="primary"
            size="md"
            onClick={() => void handleSubmit()}
            disabled={isSubmitting}
          >
            決定
          </Button>
        </>
      }
    >
      <div className="flex items-stretch">
        <div className={`w-[370px] shrink-0 pb-[18px] ${calendarClassName}`}>
          <DatePicker
            selected={selected}
            locale="ja"
            inline
            onChange={handleSelectDate}
            minDate={today}
            maxDate={max}
            renderDayContents={renderDay}
            renderCustomHeader={({
              date,
              decreaseMonth,
              increaseMonth,
              prevMonthButtonDisabled,
              nextMonthButtonDisabled,
            }) => (
              <div className="-mb-xxs flex items-center justify-center py-xs">
                <div className="flex w-[236px] items-center justify-between px-xs">
                  <button
                    type="button"
                    aria-label="前の月"
                    onClick={decreaseMonth}
                    disabled={prevMonthButtonDisabled}
                    className="flex size-6 cursor-pointer items-center justify-center border-none bg-transparent p-0 text-primary disabled:cursor-not-allowed disabled:text-border"
                  >
                    <MdKeyboardArrowLeft className="size-6" />
                  </button>
                  <span className="text-heading-xs text-sub">
                    {`${date.getFullYear()}年 ${date.getMonth() + 1}月`}
                  </span>
                  <button
                    type="button"
                    aria-label="次の月"
                    onClick={increaseMonth}
                    disabled={nextMonthButtonDisabled}
                    className="flex size-6 cursor-pointer items-center justify-center border-none bg-transparent p-0 text-primary disabled:cursor-not-allowed disabled:text-border"
                  >
                    <MdKeyboardArrowRight className="size-6" />
                  </button>
                </div>
              </div>
            )}
          />
        </div>
        <div className="relative w-16 shrink-0 border-l border-solid border-border">
          <div
            ref={hourListRef}
            role="group"
            aria-label="時"
            className="hidden-scrollbar absolute inset-0 flex flex-col items-center gap-xs overflow-y-auto py-xs"
          >
            <div aria-hidden="true" className="h-[calc(50%-16px)] shrink-0" />
            {HOURS.map((hour) => {
              const isSelected = selected.getHours() === hour;
              return (
                <button
                  key={hour}
                  type="button"
                  aria-pressed={isSelected}
                  disabled={!isHourSelectable(hour)}
                  onClick={() => {
                    const nextMinute = isWithinBounds(
                      withTime(selected, hour, selected.getMinutes())
                    )
                      ? selected.getMinutes()
                      : (MINUTES.find((minute) =>
                          isWithinBounds(withTime(selected, hour, minute))
                        ) ?? selected.getMinutes());
                    setErrorMessage(null);
                    setSelected(withTime(selected, hour, nextMinute));
                  }}
                  className={timeOptionClassName(isSelected)}
                >
                  {String(hour).padStart(2, '0')}
                </button>
              );
            })}
            <div aria-hidden="true" className="h-[calc(50%-16px)] shrink-0" />
          </div>
        </div>
        <div className="relative w-16 shrink-0 border-l border-solid border-border">
          <div
            ref={minuteListRef}
            role="group"
            aria-label="分"
            className="hidden-scrollbar absolute inset-0 flex flex-col items-center gap-xs overflow-y-auto py-xs"
          >
            <div aria-hidden="true" className="h-[calc(50%-16px)] shrink-0" />
            {MINUTES.map((minute) => {
              const isSelected = selected.getMinutes() === minute;
              return (
                <button
                  key={minute}
                  type="button"
                  aria-pressed={isSelected}
                  disabled={!isWithinBounds(withTime(selected, selected.getHours(), minute))}
                  onClick={() => {
                    setErrorMessage(null);
                    setSelected(withTime(selected, selected.getHours(), minute));
                  }}
                  className={timeOptionClassName(isSelected)}
                >
                  {String(minute).padStart(2, '0')}
                </button>
              );
            })}
            <div aria-hidden="true" className="h-[calc(50%-16px)] shrink-0" />
          </div>
        </div>
      </div>
    </MailDialog>
  );
}
